// Agrupa la agenda por proyecto, para la pantalla "Qué hago ahora".
//
// Antes la pantalla agrupaba sólo por fecha, así que las tareas de todos los
// proyectos se mezclaban en una lista larga. Acá se arma la otra vista: una
// tarjeta por proyecto, con el resumen de lo que tiene adentro.
//
// Cálculo puro, sin DOM: lo mismo que fecha.ts y calculo.ts, y por la misma
// razón (AGENTS.md sección 5) va con test.

import type { ColorProyecto, ItemAgenda } from "./tipos";
import { urgencia } from "./fecha";

export interface GrupoAgenda {
  /** NULL = las notas de la Bandeja, que todavía no tienen proyecto. */
  proyecto_id: number | null;
  nombre: string;
  /** La marca de color del proyecto, o null si no le pusieron ninguna. */
  color: ColorProyecto | null;
  items: ItemAgenda[];
  vencidas: number;
  /** Vence hoy. Se cuenta aparte de `vencidas`: todavía está a tiempo. */
  hoy: number;
  semana: number;
  lejos: number;
}

export const SIN_PROYECTO = "Sin proyecto";

/** Lo que arde: vencido o venciendo hoy. Es lo que ordena la pantalla. */
export function calientes(g: GrupoAgenda): number {
  return g.vencidas + g.hoy;
}

/**
 * Una tarjeta por proyecto, de la más urgente a la más tranquila.
 *
 * **Arrancan todas cerradas.** Primero se abría una —la más urgente—, pero
 * hasta esa hacía scrollear: lo que se quiere ver al abrir la app es el mapa
 * completo, qué proyectos tienen algo encima, y el encabezado ya lo dice sin
 * desplegar nada. La que uno va a mirar se abre de un toque.
 *
 * El orden: primero lo que más arde, después lo que vence antes, y a igualdad
 * de todo, alfabético para que no baile entre recargas.
 */
export function agruparPorProyecto(items: ItemAgenda[], hoy: string): GrupoAgenda[] {
  const porClave = new Map<string, GrupoAgenda>();

  for (const item of items) {
    const clave = item.proyecto_id === null ? "sin" : String(item.proyecto_id);
    let g = porClave.get(clave);
    if (!g) {
      g = {
        proyecto_id: item.proyecto_id,
        nombre: item.proyecto_nombre ?? SIN_PROYECTO,
        color: item.proyecto_color ?? null,
        items: [],
        vencidas: 0,
        hoy: 0,
        semana: 0,
        lejos: 0,
      };
      porClave.set(clave, g);
    }
    g.items.push(item);

    const u = urgencia(item.fecha, hoy);
    if (u === "VENCIDO") g.vencidas++;
    else if (u === "HOY") g.hoy++;
    else if (u === "SEMANA") g.semana++;
    else if (u === "LEJOS") g.lejos++;
  }

  const grupos = [...porClave.values()];

  for (const g of grupos) {
    g.items.sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
  }

  grupos.sort((a, b) => {
    const arde = calientes(b) - calientes(a);
    if (arde !== 0) return arde;
    const primeraA = a.items[0]?.fecha ?? "";
    const primeraB = b.items[0]?.fecha ?? "";
    if (primeraA !== primeraB) return primeraA < primeraB ? -1 : 1;
    return a.nombre < b.nombre ? -1 : a.nombre > b.nombre ? 1 : 0;
  });

  return grupos;
}

/** Los totales del encabezado. Se cuentan sobre los grupos ya armados. */
export function totalesAgenda(grupos: GrupoAgenda[]): {
  vencidas: number;
  hoy: number;
  proyectos: number;
} {
  return {
    vencidas: grupos.reduce((n, g) => n + g.vencidas, 0),
    hoy: grupos.reduce((n, g) => n + g.hoy, 0),
    proyectos: grupos.filter((g) => calientes(g) > 0).length,
  };
}
